import { ComputedProperty } from "./ComputedProperty.js";
import { AstNode, Writer } from "./core/index.js";
import { DocComment } from "./DocComment.js";
import type { EnumWithRawValues } from "./EnumWithRawValues.js";
import { Initializer } from "./Initializer.js";
import { Method } from "./Method.js";
import { Protocol } from "./Protocol.js";
import { Struct } from "./Struct.js";

export declare namespace Extension {
    interface Args {
        name: string;
        conformances?: Protocol[];
        initializers?: Initializer[];
        computedProperties?: ComputedProperty[];
        methods?: Method[];
        nestedTypes?: (Struct | EnumWithRawValues)[];
        docs?: DocComment;
    }
}

export class Extension extends AstNode {
    public readonly name: string;
    public readonly conformances: string[];
    public readonly initializers: Initializer[];
    public readonly computedProperties: ComputedProperty[];
    public readonly methods: Method[];
    public readonly nestedTypes: (Struct | EnumWithRawValues)[];
    public readonly docs?: DocComment;

    public constructor({
        name,
        conformances,
        initializers,
        computedProperties,
        methods,
        nestedTypes,
        docs
    }: Extension.Args) {
        super();
        this.name = name;
        this.conformances = conformances ?? [];
        this.initializers = initializers ?? [];
        this.computedProperties = computedProperties ?? [];
        this.methods = methods ?? [];
        this.nestedTypes = nestedTypes ?? [];
        this.docs = docs;
    }

    public write(writer: Writer): void {
        if (this.docs != null) {
            this.docs.write(writer);
        }
        writer.write(`extension ${this.name}`);
        this.conformances.forEach((conformance, index) => {
            if (index === 0) {
                writer.write(": ");
            } else if (index > 0) {
                writer.write(", ");
            }
            writer.write(conformance);
        });
        writer.write(" {");
        if (this.initializers.length > 0) {
            writer.newLine();
            writer.indent();
            this.initializers.forEach((initializer, initializerIdx) => {
                if (initializerIdx > 0) {
                    writer.newLine();
                }
                initializer.write(writer);
                writer.newLine();
            });
            writer.dedent();
        }
        if (this.computedProperties.length > 0) {
            writer.newLine();
            writer.indent();
            this.computedProperties.forEach((computedProperty, computedPropertyIdx) => {
                if (computedPropertyIdx > 0) {
                    writer.newLine();
                }
                computedProperty.write(writer);
                writer.newLine();
            });
            writer.dedent();
        }
        if (this.methods.length > 0) {
            writer.newLine();
            writer.indent();
            this.methods.forEach((method, methodIdx) => {
                if (methodIdx > 0) {
                    writer.newLine();
                }
                method.write(writer);
                writer.newLine();
            });
            writer.dedent();
        }
        if (this.nestedTypes.length > 0) {
            writer.newLine();
            writer.indent();
            this.nestedTypes.forEach((nestedType, nestedTypeIdx) => {
                if (nestedTypeIdx > 0) {
                    writer.newLine();
                }
                nestedType.write(writer);
                writer.newLine();
            });
            writer.dedent();
        }
        writer.write("}");
    }
}
